<template>
  <div class="">
    <template v-if="!nextPage">
      <div class="flex flex-col items-center pt-8 cursor-pointer">
        <div
          class="text-[52px]"
          :style="{ color }"
          @click="() => (nextPage = true)"
        >
          <span v-if="watchfacePowerData"
            >{{ watchfacePowerData?.powerClass }}级</span
          >
          <span v-else>--</span>
        </div>
        <div class="text-lg opacity-70">表盘功耗等级</div>
      </div>
      <div
        class="flex flex-col gap-[4px] max-h-[350px] overflow-auto mt-8 bg-[#1e1e1e] p-5 rounded-2xl text-[16px]"
      >
        <div class="flex opacity-40">
          <div class="flex-[1.5] mr-16">高功耗图层</div>
          <div class="flex-1">帧率</div>
          <div class="flex-1">面积占比</div>
          <div class="flex-1">时长</div>
          <div class="flex-1">功耗等级</div>
          <div class="flex-1"></div>
        </div>
        <template v-if="watchfacePowerData?.powerClass == 1">
          <div class="flex justify-center items-center h-28">
            <span>暂无高功耗图层</span>
          </div>
        </template>
        <template v-if="(watchfacePowerData?.powerClass ?? 0) > 1">
          <div class="mt-2 overflow-auto max-h-[240px]">
            <div
              v-for="(item, key) in currenthighPowerLayers"
              :key="key"
              class="flex mt-5"
            >
              <div class="flex-[1.5] mr-16">
                <span class="text-[#787878]">
                  {{ getShapeParentName(item.shapeId) }}
                </span>
                <span>
                  {{ getShapeName(item.shapeId) }}
                </span>
              </div>
              <div class="flex-1">{{ fpsFilter(item) }}</div>
              <div class="flex-1">
                {{ areaFilter(item.areaRatio) }}
              </div>
              <div class="flex-1">重复</div>
              <div class="flex-1">{{ item.powerClass }}</div>
              <div class="flex-1">
                <a-button
                  type="back"
                  class="!w-14 !h-7 !text-[14px] !ml-10"
                  @click="handleCheck(item)"
                >
                  查看
                </a-button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- description -->
      <div
        v-if="watchfacePowerData?.reasons?.length"
        class="ml-5 mt-5 text-[#E66138]"
      >
        注意：
        <div
          v-for="(item, key) in [...new Set(watchfacePowerData.reasons)]"
          :key="key"
        >
          {{ key + 1 }}. {{ item }}
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flex justify-center">
        <div>
          <a-button
            type="back"
            class="!w-14 !h-7 !text-[14px] ml-2 mt-5 !text-[#bbb]"
            @click="() => (nextPage = false)"
          >
            返回
          </a-button>
          <div class="pt-8 -mt-3">
            <div class="flex gap-x-5">
              <div
                v-for="(item, key) in powerDescription"
                :key="key"
                class="w-50 h-45 border rounded-xl border-white/20 flex flex-col items-center justify-center text-white/20"
                :class="{
                  ' border-white/40 !border-[2px] !text-white/40 !bg-[#1e1e1e]/60':
                    +item.level[0] === watchfacePowerData?.powerClass
                }"
              >
                <div
                  class="text-[42px]"
                  :style="{ color }"
                  :class="{
                    '!text-white/40':
                      +item.level[0] !== watchfacePowerData?.powerClass
                  }"
                >
                  {{ item.level }}
                </div>
                <span>功耗{{ item.degree }}</span>
                <span>续航时长减少{{ item.scale }}</span>
              </div>
            </div>
          </div>
          <div class="ml-5 mt-5 text-[#bbb]">
            影响表盘功耗的因素: <br />
            1.动画的帧率、面积、时长<br />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import { PropType, computed, ref } from 'vue';
import { useEditorStore } from '@/store/use-editor-store';
import { Shape } from '@/services/shape';
import { LayerPowerClass, WatchfacePowerData } from '@/hooks';

const editorStore = useEditorStore();
const props = defineProps({
  watchfacePowerData: {
    type: Object as PropType<WatchfacePowerData>,
    default: undefined
  }
});

const emit = defineEmits<{
  (event: 'close'): void;
}>();

const areaFilter = (val?: number) => {
  if (!val) return '-';
  return val.toFixed(1) + '%';
};
const fpsFilter = (item: LayerPowerClass) => {
  const { fps, ruleType } = item;
  if (ruleType == 'rive') {
    return '满帧';
  } else {
    return fps ? Math.round(fps) : '-';
  }
};

const nextPage = ref<boolean>(false);
const color = computed(() => {
  const _powerClass = props.watchfacePowerData?.powerClass ?? 0;
  return _powerClass == 0
    ? 'white'
    : _powerClass == 1
    ? '#52E973'
    : _powerClass == 2
    ? '#FF8A05'
    : '#FF4343';
});

const currenthighPowerLayers = computed(() => {
  return props.watchfacePowerData?.highPowerLayers;
});

const getShapeName = (shapeId: string) => {
  const shape = Shape.getShape(shapeId);
  return shape?.attrs.name;
};
const getShapeParentName = (shapeId: string) => {
  const names: string[] = [];
  const shape = Shape.getShape(shapeId);
  let _parent = shape?.parent;
  while (_parent) {
    names.unshift(_parent.attrs.name ?? '');
    _parent = _parent.parent;
  }
  const name = names.join('>');
  return name ? name + '>' : '';
};

const handleCheck = (item: LayerPowerClass) => {
  editorStore.selectTheme(item.themeId);
  if (item.colorId) {
    editorStore.selectThemeColor(item.colorId);
  }
  editorStore.select([item.shapeId]);
  emit('close');
};

const powerDescription: {
  level: string;
  degree: string;
  scale: string;
}[] = [
  {
    level: '1级',
    degree: '低',
    scale: '12%以下'
  },
  {
    level: '2级',
    degree: '中等',
    scale: '12%-18%'
  },
  {
    level: '3级',
    degree: '高',
    scale: '18%以上'
  }
];
</script>
<style scoped></style>
